import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BOAPI } from '../service/UsersService';
import utils from '../utils/utils';

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-');
const fmtMoney = (n) => `₪${Number(n || 0).toLocaleString('he-IL', { maximumFractionDigits: 2 })}`;
const fullUrl = (url) => (!url ? '' : /^https?:/i.test(url) ? url : `${utils.BaseUrl}${url.startsWith('/') ? '' : '/'}${url}`);

const ROLE_LABEL = { owner: 'בעלים', manager: 'מנהל', employee: 'עובד' };
const STATUS_META = {
  active:    { label: 'פעיל', color: '#1f6feb', bg: '#eef4ff' },
  expired:   { label: 'פג תוקף', color: '#697386', bg: '#f1f3f5' },
  redeemed:  { label: 'מומש', color: '#2e7d32', bg: '#e8f5e9' },
  cancelled: { label: 'בוטל', color: '#d93838', bg: '#fff1f1' },
};
const STATUS_FILTERS = [
  { key: '', label: 'הכל' },
  { key: 'redeemed', label: 'מומשו' },
  { key: 'active', label: 'פעילים' },
  { key: 'expired', label: 'פגי תוקף' },
  { key: 'cancelled', label: 'בוטלו' },
];

// דף בית עסק בבק אופיס (/businesses/:id) - נפתח מלחיצה על שם בית עסק בטבלת
// המשתמשים או בטאב "בתי עסק". פרטים, בעלים ועובדים (כולל מי שעוד לא התחבר),
// סטטיסטיקה, וכל הקופונים שהונפקו - פעילים, פגי תוקף, מומשו ובוטלו
// (spBOGetBusinessDetails). מחזור/עמלה נספרים רק על עסקאות שמומשו ולא בוטלו
const BusinessDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [wooBusy, setWooBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const resp = await BOAPI.fetchBusinessDetails(Number(id));
        if (resp?.[0]?.[0]?.code === 0) {
          setData({
            business: resp[1]?.[0] || null,
            staff: resp[2] || [],
            stats: resp[3]?.[0] || {},
            transactions: resp[4] || [],
            discountItems: resp[5] || [],
          });
        } else {
          setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת בית העסק');
        }
      } catch {
        setError('שגיאה בתקשורת עם השרת');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const visibleTransactions = useMemo(
    () => (data?.transactions || []).filter((t) => !statusFilter || t.displayStatus === statusFilter),
    [data, statusFilter]
  );

  if (loading) return <div style={s.page}><div style={s.center}>טוען...</div></div>;
  if (error || !data?.business) return <div style={s.page}><div style={s.errorBanner}>❌ {error || 'בית העסק לא נמצא'}</div></div>;

  const { business: b, staff, stats, discountItems } = data;
  const categories = b.categoryLabels?.length ? b.categoryLabels : [b.categoryLabel];

  return (
    <div style={s.page}>
      <button style={s.backBtn} onClick={() => navigate(-1)}>→ חזרה</button>

      {/* ── כותרת ── */}
      <div style={s.card}>
        <div style={s.header}>
          {b.logoUrl ? <img src={fullUrl(b.logoUrl)} alt="" style={s.logo} /> : <div style={s.logoPlaceholder}>{(b.name || '?')[0]}</div>}
          <div style={{ flex: 1 }}>
            <h1 style={s.title}>{b.name}</h1>
            <div style={s.chips}>
              {categories.map((c) => <span key={c} style={s.chip}>{c}</span>)}
              <span style={{ ...s.chip, ...(b.active ? s.chipGreen : s.chipGray) }}>{b.active ? 'פעיל' : 'מושבת'}</span>
              <span style={{ ...s.chip, ...(b.ownerConfirmedAt ? s.chipGreen : s.chipOrange) }}>{b.ownerConfirmedAt ? 'אושר ע"י הבעלים' : 'ממתין לאישור בעלים'}</span>
              {b.wooConnected && <span style={{ ...s.chip, ...s.chipBlue }}>🛒 מחובר ל-WooCommerce</span>}
            </div>
          </div>
        </div>
        <div style={s.infoGrid}>
          <Info label="הנחה כללית" value={`${b.discountPercent}%`} />
          <Info label="עמלת סליקה" value={`${b.commissionPercent ?? 0}%`} />
          <Info label="כתובת" value={[b.address, b.city].filter(Boolean).join(', ') || '-'} />
          <Info label="טלפון העסק" value={b.phone || '-'} />
          <Info label="הצטרף" value={fmtDateTime(b.iTime)} />
          {b.onlineStoreUrl && <Info label="אתר" value={<a href={b.onlineStoreUrl} target="_blank" rel="noreferrer">{b.onlineStoreUrl}</a>} />}
          {b.wooConnected && <Info label="התוסף התחבר לאחרונה" value={fmtDateTime(b.wooLastSeenAt)} />}
        </div>
        {b.benefitDescription && <p style={s.benefit}>פירוט ההטבה: {b.benefitDescription}</p>}

        {/* הזמנה לחבר חנות אונליין - בלי זה בעל העסק לא רואה את האפשרות באפליקציה */}
        <div style={s.wooBox}>
          <div style={{ flex: 1 }}>
            <strong>חנות אונליין (WooCommerce)</strong>
            <div style={s.sub}>
              {b.wooEnabled
                ? 'מופעל - בעל העסק רואה באפליקציה את האפשרות לחבר את האתר שלו.'
                : 'כבוי - האפשרות מוסתרת מבעל העסק. הפעילו כדי להזמין אותו לחבר את חנות האונליין שלו.'}
              {b.wooConnected && !b.wooEnabled && ' שימו לב: התוסף באתר מחובר אבל מושבת - קודי איגוד לא יעבדו באתר עד להפעלה מחדש.'}
            </div>
          </div>
          <button
            style={{ ...s.wooToggle, ...(b.wooEnabled ? s.wooToggleOff : s.wooToggleOn) }}
            disabled={wooBusy}
            onClick={async () => {
              setWooBusy(true);
              try {
                const resp = await BOAPI.setBusinessWooEnabled(b.id, !b.wooEnabled);
                if (resp?.[0]?.[0]?.code === 0) {
                  setData((prev) => ({ ...prev, business: { ...prev.business, wooEnabled: !!resp[1]?.[0]?.wooEnabled } }));
                } else {
                  window.alert(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
                }
              } finally {
                setWooBusy(false);
              }
            }}
          >
            {wooBusy ? '...' : b.wooEnabled ? 'כיבוי' : 'הפעלה'}
          </button>
        </div>
      </div>

      {/* ── סטטיסטיקה ── */}
      <div style={s.stats}>
        <Stat label="קופונים שהונפקו" value={stats.issuedCount || 0} color="#1a1f36" />
        <Stat label="מומשו" value={stats.redeemedCount || 0} color="#2e7d32" />
        <Stat label="פעילים כרגע" value={stats.activeCount || 0} color="#1f6feb" />
        <Stat label="פגו בלי מימוש" value={stats.expiredCount || 0} color="#697386" />
        <Stat label="בוטלו" value={stats.cancelledCount || 0} color="#d93838" />
        <Stat label="מחזור" value={fmtMoney(stats.salesAmount)} color="#1f6feb" />
        <Stat label="הנחות ללקוחות" value={fmtMoney(stats.discountAmount)} color="#a06d00" />
        <Stat label="עמלה (סה״כ)" value={fmtMoney(stats.commissionAmount)} color="#8B5CF6" />
        <Stat label="עמלה החודש" value={fmtMoney(stats.commissionThisMonth)} color="#8B5CF6" />
      </div>

      <div style={s.twoCols} className="bd-two-cols">
        <style>{`@media (max-width: 900px) { .bd-two-cols { grid-template-columns: 1fr !important; } }`}</style>

        {/* ── בעלים ועובדים ── */}
        <div style={s.card}>
          <h3 style={s.sectionTitle}>בעלים ועובדים ({staff.length})</h3>
          {staff.length === 0 ? (
            <div style={s.muted}>אין עדיין בעלים משויך (ממתין לאישור קישור)</div>
          ) : (
            staff.map((m) => (
              <div key={`${m.userId}-${m.role}`} style={s.staffRow}>
                <div>
                  <button style={s.linkBtn} onClick={() => navigate(`/users/${m.userId}`)}>{m.name || 'ללא שם'}</button>
                  <div style={s.sub}>{m.phone}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {m.pendingInvite && <span style={{ ...s.chip, ...s.chipOrange }}>הוזמן - טרם התחבר</span>}
                  <span style={{ ...s.chip, ...(m.role === 'owner' ? s.chipBlue : {}) }}>{ROLE_LABEL[m.role] || m.role}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── הנחות לפי מוצר ── */}
        <div style={s.card}>
          <h3 style={s.sectionTitle}>הנחות לפי מוצר</h3>
          {discountItems.length === 0 ? (
            <div style={s.muted}>הנחה אחידה של {b.discountPercent}% לכל המוצרים</div>
          ) : (
            <>
              {discountItems.map((d, i) => (
                <div key={i} style={s.staffRow}>
                  <span>{d.label}</span>
                  <strong>{d.discountPercent > 0 ? `${d.discountPercent}%` : 'ללא הנחה'}</strong>
                </div>
              ))}
              <div style={s.staffRow}>
                <span style={s.muted}>שאר המוצרים</span>
                <strong>{b.discountPercent}%</strong>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── קופונים ועסקאות ── */}
      <div style={s.card}>
        <div style={s.tableHeader}>
          <h3 style={{ ...s.sectionTitle, margin: 0 }}>קופונים ועסקאות</h3>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map((f) => (
              <button key={f.key} onClick={() => setStatusFilter(f.key)} style={{ ...s.filterBtn, ...(statusFilter === f.key ? s.filterBtnActive : {}) }}>{f.label}</button>
            ))}
          </div>
        </div>

        {visibleTransactions.length === 0 ? (
          <div style={s.muted}>אין קופונים להצגה</div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>הונפק</th>
                  <th style={s.th}>לקוח</th>
                  <th style={s.th}>קוד</th>
                  <th style={s.th}>סטטוס</th>
                  <th style={s.th}>מומש</th>
                  <th style={s.th}>ערוץ</th>
                  <th style={s.th}>סכום</th>
                  <th style={s.th}>הנחה</th>
                  <th style={s.th}>עמלה</th>
                </tr>
              </thead>
              <tbody>
                {visibleTransactions.map((t) => {
                  const meta = STATUS_META[t.displayStatus] || STATUS_META.active;
                  const cancelled = t.status === 'cancelled';
                  return (
                    <tr key={t.id}>
                      <td style={s.td}>{fmtDateTime(t.issuedAt)}</td>
                      <td style={s.td}>{t.customerName}<div style={s.sub}>{t.customerPhone}</div></td>
                      <td style={{ ...s.td, fontFamily: 'monospace' }}>{t.code}</td>
                      <td style={s.td}>
                        <span style={{ ...s.chip, background: meta.bg, color: meta.color }}>{meta.label}</span>
                        {cancelled && <div style={s.sub}>{fmtDateTime(t.cancelledAt)}{t.cancelReason ? ` · ${t.cancelReason}` : ''}</div>}
                        {t.displayStatus === 'active' && <div style={s.sub}>בתוקף עד {fmtDateTime(t.expiresAt)}</div>}
                      </td>
                      <td style={s.td}>
                        {t.redeemedAt ? fmtDateTime(t.redeemedAt) : '-'}
                        {t.redeemedByName && <div style={s.sub}>ע"י {t.redeemedByName}</div>}
                      </td>
                      <td style={s.td}>{t.channel === 'online' ? <>🛒 אתר{t.wooOrderId ? <div style={s.sub}>#{t.wooOrderId}</div> : null}</> : t.redeemedAt ? '🏪 חנות' : '-'}</td>
                      <td style={{ ...s.td, ...(cancelled ? s.strike : {}) }}>{t.amount != null ? fmtMoney(t.amount) : t.declaredAmount != null ? <span style={s.sub}>משוער {fmtMoney(t.declaredAmount)}</span> : '-'}</td>
                      <td style={{ ...s.td, ...(cancelled ? s.strike : {}) }}>{t.discountAmount != null ? fmtMoney(t.discountAmount) : `${t.discountPercent}%`}</td>
                      <td style={{ ...s.td, ...(cancelled ? s.strike : {}) }}>{t.commissionAmount != null ? fmtMoney(t.commissionAmount) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <div style={s.infoLabel}>{label}</div>
    <div style={s.infoValue}>{value}</div>
  </div>
);

const Stat = ({ label, value, color }) => (
  <div style={s.stat}>
    <div style={{ ...s.statValue, color }}>{value}</div>
    <div style={s.statLabel}>{label}</div>
  </div>
);

const s = {
  page:         { direction: 'rtl', fontFamily: 'system-ui, -apple-system, sans-serif', background: '#f6f8fb', minHeight: '100vh', padding: '1.5rem', maxWidth: 1300, margin: '0 auto' },
  backBtn:      { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, marginBottom: 14 },
  center:       { padding: 60, textAlign: 'center', color: '#999' },
  errorBanner:  { background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6 },
  card:         { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 16, padding: 20, marginBottom: 16 },
  header:       { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16 },
  logo:         { width: 72, height: 72, objectFit: 'contain', borderRadius: 12, border: '1px solid #e3e8ee', background: '#fff' },
  logoPlaceholder: { width: 72, height: 72, borderRadius: 12, background: '#fdf1e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, color: '#a6552a' },
  title:        { margin: '0 0 8px', fontSize: 24, color: '#1a1f36' },
  chips:        { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip:         { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, background: '#f1f3f5', color: '#4f566b' },
  chipGreen:    { background: '#e8f5e9', color: '#2e7d32' },
  chipGray:     { background: '#f1f3f5', color: '#697386' },
  chipOrange:   { background: '#fff8e1', color: '#a06d00' },
  chipBlue:     { background: '#eef4ff', color: '#1f6feb' },
  infoGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 },
  infoLabel:    { fontSize: 12, color: '#8792a2', marginBottom: 2 },
  infoValue:    { fontSize: 14, color: '#1a1f36', fontWeight: 600, wordBreak: 'break-word' },
  benefit:      { margin: '14px 0 0', color: '#4f566b', fontSize: 13.5 },
  wooBox:       { display: 'flex', alignItems: 'center', gap: 14, marginTop: 16, padding: '12px 14px', background: '#f6f8fb', borderRadius: 12, fontSize: 14, flexWrap: 'wrap' },
  wooToggle:    { padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, border: '1px solid' },
  wooToggleOn:  { background: '#1f6feb', color: '#fff', borderColor: '#1f6feb' },
  wooToggleOff: { background: '#fff', color: '#d93838', borderColor: '#fbc4c4' },
  stats:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 },
  stat:         { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 12, padding: '12px 14px' },
  statValue:    { fontSize: 20, fontWeight: 800 },
  statLabel:    { fontSize: 12, color: '#697386', marginTop: 2 },
  twoCols:      { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 },
  sectionTitle: { margin: '0 0 12px', fontSize: 17, color: '#1a1f36' },
  staffRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid #f1f3f5', fontSize: 14 },
  linkBtn:      { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1f6feb', fontWeight: 700, fontSize: 14 },
  sub:          { color: '#8792a2', fontSize: 11.5, marginTop: 2 },
  muted:        { color: '#8792a2', fontSize: 13.5 },
  tableHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  filterBtn:    { padding: '6px 12px', background: '#fff', border: '1.5px solid #e3e8ee', borderRadius: 16, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#697386' },
  filterBtnActive: { background: '#eef4ff', color: '#1f6feb', borderColor: '#1f6feb' },
  tableWrap:    { overflowX: 'auto', border: '1px solid #e3e8ee', borderRadius: 12 },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:           { textAlign: 'right', padding: '10px 12px', background: '#f6f8fb', color: '#4f566b', fontWeight: 700, whiteSpace: 'nowrap', borderBottom: '1px solid #e3e8ee' },
  td:           { textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top', color: '#1a1f36' },
  strike:       { textDecoration: 'line-through', color: '#a3acb9' },
};

export default BusinessDetails;
